<script>
	import risk from './points.js';

	const R = risk[0];

	// Grouped so the list reads as clinical categories rather than fifteen
	// undifferentiated switches. Keys are unchanged - they index the model.
	const GROUPS = [
		{
			name: 'Movement',
			items: [
				{ key: 'trem', label: 'Tremor' },
				{ key: 'rigi', label: 'Rigidity' },
				{ key: 'bala', label: 'Balance problem' }
			]
		},
		{
			name: 'Mood and thinking',
			items: [
				{ key: 'depr', label: 'Depression or anxiety' },
				{ key: 'memo', label: 'Memory problem' },
				{ key: 'cogn', label: 'Cognitive decline' },
				{ key: 'fati', label: 'Fatigue' }
			]
		},
		{
			name: 'Body functions',
			items: [
				{ key: 'cons', label: 'Constipation' },
				{ key: 'urin', label: 'Urinary dysfunction' },
				{ key: 'hypo', label: 'Low blood pressure' },
				{ key: 'dizz', label: 'Dizziness' },
				{ key: 'hype', label: 'Excess saliva' }
			]
		}
	];

	// Smoking is one of three mutually exclusive states, not two checkboxes.
	// As checkboxes a person could tick BOTH ex-smoker and current smoker, and
	// the model summed both coefficients (-0.33 and -0.86, both protective in
	// this model). For a 72-year-old man with four symptoms that displayed
	// 6.79% against a true 9.20% (current) or 14.68% (ex) — a contradictory
	// input silently UNDERSTATING a patient's risk. A radio group makes the
	// impossible state unrepresentable.
	const SMOKING = [
		{ value: 'never', label: 'Never smoked' },
		{ value: 'exsm', label: 'Ex-smoker' },
		{ value: 'curr', label: 'Current smoker' }
	];

	let age = 60;
	let sex = 'f';
	let picked = {};
	let smoking = 'never'; // 'never' carries no coefficient; the model's reference class

	// NOT `bind:checked={picked[key]}`. Binding to an object MEMBER writes
	// through without reassigning the object, so Svelte never invalidates it
	// and the risk would not recompute as boxes were ticked. Spreading into a
	// new object makes the dependency explicit.
	function toggle(key) {
		picked = { ...picked, [key]: !picked[key] };
	}

	function baseline(a, s) {
		const band = a < 60 ? '50' : a < 70 ? '60' : a < 80 ? '70' : a < 90 ? '80' : '90';
		return R[(s === 'm' ? 'm' : 'f') + band];
	}

	$: validAge = Number.isFinite(+age) && +age >= 50 && +age <= 100;
	$: logit = validAge
		? GROUPS.reduce(
				(sum, g) =>
					sum + g.items.reduce((s, it) => s + (picked[it.key] ? R[it.key] : 0), 0),
				baseline(+age, sex)
		  ) + (smoking === 'never' ? 0 : +R[smoking])
		: NaN;
	$: probability = validAge
		? Math.round((1 / (1 + Math.exp(-logit))) * 10000) / 100
		: null;

	// No JS motion state here: this tool's only animation is the CSS bar-fill
	// width, which the prefers-reduced-motion block at the bottom disables.
	//
	// The headline figure is rendered DIRECTLY, not tweened. A count-up depends
	// on requestAnimationFrame; in a throttled or backgrounded iframe it can
	// stall at its start value, and a risk calculator that displays "0.00%"
	// next to a sentence reading "about 22 in 100" is worse than one that
	// simply does not animate. The bar carries the motion instead, and a
	// stalled CSS width transition still ends at the right place.

	// A plain linear 0-100% bar: the fill IS the risk. An earlier version
	// capped the track at 25% to make small results readable, but the model's
	// reachable output runs 0.02% to 99.99% (a 78-year-old with tremor,
	// rigidity and cognitive decline sits well past 50%), so the cap hid the top
	// three-quarters of the scale and needed a label, an over-cap colour and an
	// accessible-name caveat to stay honest. All of that was scaffolding for a
	// distortion. The number and the "1 in 1,000" sentence carry the low end.
	//
	// min-width on the fill keeps a 0.14% result from vanishing to sub-pixel:
	// it says "nearly empty", which is true, rather than "no bar", which is not.
	$: barPct = probability === null ? 0 : probability;

	// Natural frequency, with the DENOMINATOR chosen so the numerator is a
	// whole number of at least 1. Rounding to "x out of 100" was wrong at the
	// low end: a real 0.2% risk read as "about 1 out of 100", overstating it
	// fivefold and contradicting the 0.20% printed directly above it.
	$: freq =
		probability === null
			? null
			: probability >= 1
			? { n: Math.round(probability), per: 100 }
			: probability >= 0.1
			? { n: Math.round(probability * 10), per: 1000 }
			: { n: null, per: 1000 };
</script>

<div class="pdrisk">
	<header class="masthead">
		<p class="eyebrow">Calculator</p>
		<h1>Parkinson&rsquo;s Disease Risk</h1>
		<p class="lede">
			Estimates the chance of developing Parkinson&rsquo;s within five years,
			for adults over 50.
		</p>
	</header>

	<div class="about">
		<div class="field">
			<label for="pdr-age">Age <span class="unit">years</span></label>
			<div class="control">
				<input id="pdr-age" type="number" bind:value={age} min="50" max="100" />
				<input
					class="slider"
					type="range"
					bind:value={age}
					min="50"
					max="100"
					aria-label="Age in years"
				/>
			</div>
		</div>

		<div class="field">
			<span class="label-text" id="pdr-sex-label">Sex</span>
			<div class="segment" role="radiogroup" aria-labelledby="pdr-sex-label">
				<label class="seg">
					<input type="radio" bind:group={sex} value="f" name="sex" />
					<span class="seg-box">Female</span>
				</label>
				<label class="seg">
					<input type="radio" bind:group={sex} value="m" name="sex" />
					<span class="seg-box">Male</span>
				</label>
			</div>
		</div>
	</div>

	<div class="symptoms">
		<p class="prompt">Tick anything you have experienced.</p>
		{#each GROUPS as group}
			<fieldset class="group">
				<legend class="group-name">{group.name}</legend>
				<div class="choices">
					{#each group.items as item}
						<label class="choice">
							<input
								type="checkbox"
								checked={!!picked[item.key]}
								on:change={() => toggle(item.key)}
							/>
							<span class="mark" aria-hidden="true">
								<svg viewBox="0 0 12 12"><path d="M2 6.5 5 9.5 10 3" /></svg>
							</span>
							<span class="choice-text">{item.label}</span>
						</label>
					{/each}
				</div>
			</fieldset>
		{/each}

		<!-- Radio, not checkboxes: exactly one of three states. See SMOKING. -->
		<fieldset class="group">
			<legend class="group-name">Smoking</legend>
			<div class="segment segment-wrap" role="radiogroup" aria-label="Smoking status">
				{#each SMOKING as opt}
					<label class="seg">
						<input type="radio" bind:group={smoking} value={opt.value} name="smoking" />
						<span class="seg-box">{opt.label}</span>
					</label>
				{/each}
			</div>
		</fieldset>
	</div>

	<section class="result" aria-live="polite">
		<p class="result-label">Estimated 5-year risk</p>
		{#if probability !== null}
			<p class="score">
				<span class="score-n">{probability.toFixed(2)}</span><span class="score-pc">%</span>
			</p>
			<div class="bar" role="img" aria-label="{probability.toFixed(2)}% of 100%">
				<span class="bar-fill" style="width: {barPct}%"></span>
			</div>
			<p class="reading">
				{#if freq.n === null}
					Fewer than <strong>1 in 1,000</strong> people with this profile would
					be expected to develop Parkinson&rsquo;s disease within five years.
				{:else}
					About <strong>{freq.n} in {freq.per.toLocaleString()}</strong> people
					with this profile would be expected to develop Parkinson&rsquo;s
					disease within five years.
				{/if}
			</p>
		{:else}
			<p class="score"><span class="score-n muted">&mdash;</span></p>
			<p class="reading">Enter an age between 50 and 100.</p>
		{/if}
		<p class="source">
			Model derived from 8,166 primary-care patients over 50 in the UK &middot;
			<cite>Schrag&nbsp;et&nbsp;al., 2019</cite>
		</p>
	</section>
</div>

<style>
	.pdrisk {
		max-width: 880px;
		margin: 0 auto;
		padding: var(--space-md) var(--space-md) var(--space-xl);
		color: var(--ink);
		font-family: 'Instrument Sans', -apple-system, BlinkMacSystemFont,
			'Segoe UI', Roboto, sans-serif;
		font-size: 16px;
		line-height: 1.5;
	}

	.masthead {
		margin-bottom: var(--space-lg);
	}
	.eyebrow {
		margin: 0 0 var(--space-xs);
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: var(--teal-text);
	}
	h1 {
		margin: 0 0 var(--space-sm);
		font-size: clamp(24px, 3.2vw, 32px);
		font-weight: 650;
		line-height: 1.15;
		letter-spacing: -0.015em;
		color: var(--ink);
	}
	.lede {
		margin: 0;
		max-width: 52ch;
		color: var(--text-2);
	}

	/* ---- age + sex ------------------------------------------------------ */
	.about {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
		gap: var(--space-lg);
		margin-bottom: var(--space-lg);
	}
	.field label,
	.label-text {
		display: block;
		margin-bottom: var(--space-xs);
		font-size: 13px;
		font-weight: 600;
		letter-spacing: 0.02em;
	}
	.unit {
		font-weight: 500;
		color: var(--text-2);
	}
	.control {
		display: flex;
		align-items: center;
		gap: var(--space-sm);
	}
	.control input[type='number'] {
		width: 76px;
		min-height: 44px;
		margin: 0;
		padding: 8px 10px;
		border: 1.5px solid var(--line);
		border-radius: 8px;
		background: var(--paper);
		font: inherit;
		font-size: 16px;
		font-variant-numeric: tabular-nums;
		color: var(--ink);
	}
	.control input[type='number']:focus-visible {
		outline: 2px solid var(--teal);
		outline-offset: 1px;
		border-color: var(--teal);
	}
	.slider {
		flex: 1;
		/* 44px of grabbable height; track and thumb stay centred inside it. */
		height: 44px;
		margin: 0;
		padding: 0;
		border: 0;
		background: transparent;
		-webkit-appearance: none;
		appearance: none;
		cursor: pointer;
	}
	.slider::-webkit-slider-runnable-track {
		height: 4px;
		border-radius: 2px;
		background: var(--line);
	}
	.slider::-moz-range-track {
		height: 4px;
		border-radius: 2px;
		background: var(--line);
	}
	.slider::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		width: 20px;
		height: 20px;
		margin-top: -8px;
		border-radius: 50%;
		border: 2px solid #fff;
		background: var(--teal);
		box-shadow: 0 1px 3px rgba(1, 77, 88, 0.35);
	}
	.slider::-moz-range-thumb {
		width: 20px;
		height: 20px;
		border-radius: 50%;
		border: 2px solid #fff;
		background: var(--teal);
	}
	.slider:focus-visible {
		outline: 2px solid var(--teal);
		outline-offset: 4px;
	}

	.segment {
		display: inline-flex;
		gap: 6px;
	}
	/* Three options: wrap on a narrow phone instead of overflowing sideways.
	   Each .seg-box keeps its 84px minimum, so at 280px they stack 2 + 1. */
	.segment-wrap {
		display: flex;
		flex-wrap: wrap;
	}
	.segment-wrap .seg {
		flex: 1 1 auto;
	}
	.segment-wrap .seg-box {
		width: 100%;
	}
	.seg {
		position: relative;
		cursor: pointer;
	}
	.seg input {
		position: absolute;
		opacity: 0;
		pointer-events: none;
	}
	.seg-box {
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: 84px;
		min-height: 44px; /* a real touch target */
		padding: 9px 14px;
		border: 1.5px solid var(--line);
		border-radius: 8px;
		background: var(--paper);
		text-align: center;
		font-size: 14px;
		color: var(--text-2);
		transition: border-color 0.18s ease-out, background-color 0.18s ease-out,
			color 0.18s ease-out;
	}
	.seg:hover .seg-box {
		border-color: var(--teal);
	}
	.seg input:checked ~ .seg-box {
		border-color: var(--teal);
		background: var(--wash);
		color: var(--teal-deep);
		font-weight: 600;
	}
	.seg input:focus-visible ~ .seg-box {
		outline: 2px solid var(--teal);
		outline-offset: 2px;
	}

	/* ---- symptoms ------------------------------------------------------- */
	.prompt {
		margin: 0 0 var(--space-md);
		font-size: 15px;
		font-weight: 600;
	}
	.group {
		margin: 0 0 var(--space-md);
		padding: 0;
		border: 0;
	}
	.group-name {
		padding: 0;
		margin-bottom: var(--space-xs);
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--text-2);
	}
	.choices {
		display: grid;
		/* align-items: start, explicitly. A grid's default is stretch, which is
		   invisible in Chrome because an auto row hugs its content — but on iOS
		   Safari the row can come out taller than its cards (seen on a real
		   iPhone: unanswered steps rendered with cards stretched to ~450px,
		   snapping to normal once answered). Start makes each card its own
		   content height whatever the row does, so the fix does not depend on
		   which WebKit quirk produced the tall row. */
		align-items: start;
		/* grid-auto-rows: max-content, explicitly. Reproduced in real WebKit
		   (Playwright, iPhone geometry, the live Webflow modal): on first layout
		   WebKit sized these auto rows to 216px and 118px for 60px and 44px
		   cards — no rule set a height, no child was tall, the track sizing
		   itself was wrong — and a click's relayout corrected it. Seen on a real
		   iPhone as a large blank gap between cards that vanished on tap.
		   max-content sizes each row to its tallest item's content through the
		   explicit path, which was correct on first paint in the same bench. */
		grid-auto-rows: max-content;
		grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
		gap: 6px;
	}
	.choice {
		position: relative;
		display: flex;
		align-items: center;
		gap: 9px;
		min-height: 44px; /* a real touch target */
		padding: 9px 12px;
		border: 1.5px solid var(--line);
		border-radius: 8px;
		background: var(--paper);
		cursor: pointer;
		transition: border-color 0.18s ease-out, background-color 0.18s ease-out;
	}
	.choice:hover {
		border-color: var(--teal);
	}
	.choice input {
		position: absolute;
		opacity: 0;
		pointer-events: none;
	}
	.mark {
		flex: none;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 18px;
		height: 18px;
		border-radius: 5px;
		border: 1.5px solid var(--line);
		background: #fff;
		transition: background-color 0.18s ease-out, border-color 0.18s ease-out;
	}
	.mark svg {
		width: 11px;
		height: 11px;
		opacity: 0;
		transition: opacity 0.15s ease-out;
	}
	.mark svg path {
		fill: none;
		stroke: #fff;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
	.choice input:checked ~ .mark {
		background: var(--teal);
		border-color: var(--teal);
	}
	.choice input:checked ~ .mark svg {
		opacity: 1;
	}
	.choice input:checked ~ .choice-text {
		color: var(--teal-deep);
		font-weight: 600;
	}
	.pdrisk .choice:has(input:checked) {
		border-color: var(--teal);
		background: var(--wash);
	}
	.choice input:focus-visible ~ .mark {
		outline: 2px solid var(--teal);
		outline-offset: 2px;
	}
	.choice-text {
		font-size: 14px;
		line-height: 1.35;
	}

	/* ---- result --------------------------------------------------------- */
	.result {
		margin-top: var(--space-lg);
		border-top: 1px solid var(--line);
		padding-top: var(--space-lg);
	}
	.result-label {
		margin: 0;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: var(--teal-text);
	}
	.score {
		margin: 2px 0 var(--space-sm);
		display: flex;
		align-items: baseline;
		gap: 2px;
	}
	.score-n {
		font-size: 64px;
		font-weight: 700;
		line-height: 1;
		letter-spacing: -0.03em;
		font-variant-numeric: tabular-nums;
		color: var(--ink);
	}
	.score-n.muted {
		color: var(--line);
	}
	.score-pc {
		font-size: 24px;
		font-weight: 600;
		color: var(--text-2);
	}
	.bar {
		height: 6px;
		max-width: 420px;
		margin: 0 0 var(--space-md);
		border-radius: 3px;
		background: var(--line);
		overflow: hidden;
	}

	/* Deliberately animates width, not transform. transform/clip-path would be
	   cheaper, but both encode the value in a property that degrades to "full"
	   if it is ever dropped or unsupported — a 6% risk silently rendering as
	   100%. Here the bar's length IS the datum, so it stays real geometry. The
	   cost is one relayout of a 6px childless leaf inside a fixed-width parent:
	   nothing propagates, and it is unmeasurable. Rounding the fill's own right
	   cap also survives, which scaleX squashes. */
	.bar-fill {
		display: block;
		min-width: 2px; /* a 0.14% result is still a bar, not an absence */
		height: 100%;
		border-radius: 3px;
		background: var(--teal);
		transition: width 0.5s var(--ease-out);
	}
	.reading {
		margin: 0;
		max-width: 52ch;
		font-size: 16px;
		line-height: 1.55;
	}
	.reading strong {
		color: var(--teal-deep);
		font-weight: 700;
	}
	.source {
		margin: var(--space-md) 0 0;
		padding-top: var(--space-sm);
		border-top: 1px solid var(--line);
		max-width: 52ch;
		font-size: 12px;
		line-height: 1.45;
		color: var(--text-2);
	}
	.source cite {
		font-style: normal;
		font-weight: 600;
		color: var(--ink);
	}

	/* ---- phones --------------------------------------------------------- */
	@media (max-width: 480px) {
		.pdrisk {
			padding: var(--space-sm) var(--space-md) var(--space-md);
			font-size: 15px;
		}
		.masthead {
			margin-bottom: var(--space-md);
		}
		h1 {
			font-size: 22px;
		}
		.lede {
			font-size: 14px;
		}
		.about {
			gap: var(--space-md);
			margin-bottom: var(--space-md);
		}
		.prompt {
			font-size: 14px;
			margin-bottom: var(--space-sm);
		}
		.choices {
			grid-template-columns: repeat(auto-fit, minmax(min(150px, 100%), 1fr));
		}
		.choice {
			padding: 8px 10px;
			gap: 7px;
		}
		.choice-text {
			font-size: 13px;
		}
		.result {
			margin-top: var(--space-md);
			padding-top: var(--space-md);
		}
		.score-n {
			font-size: 52px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.choice,
		.mark,
		.mark svg,
		.seg-box,
		.bar-fill {
			transition: none;
		}
	}
</style>
